import React, { FC, useRef } from 'react';
import { FontColor, Headline2, Headline4, TextDefault, TextTiny } from '../../Text';
import { Icon } from '../../Icon';
import { Flexbox, FlexboxVertical } from '../../Layout';
import styled from 'styled-components';
import { Button } from '../../Button';
import { PanelWithGradient } from '../../PanelWithGradient';
import { LinkButton } from '../../LinkButton';
import { Product } from '../../../../interfaces/Product';
import { parseProductColorSet } from '../icons/ProductIconWrapper';
import { AnimatedContainer, AnimatedWhenVisible, calculateAnimationDelayItemsAppear } from '../../AnimatedContainer';

const SummaryWrapper = styled(FlexboxVertical)`
  margin: 4rem auto;
  
  ${Headline2} {
    text-align: center;
    margin-bottom: 1rem;
  }

  ${Headline4} {
    margin-bottom: 1.5rem;
  }
  
  ${PanelWithGradient} {
    ul {
      list-style: none;

      li {
        margin-bottom: .5rem;
        
        ${TextDefault} {
          margin-left: 1rem;        
        }
      }
      
      *:last-child {
        li {
          margin-bottom: 0;
        }
      }
    }
  }
`;

const ListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const ButtonsWrapper = styled(Flexbox)`
  margin-top: 2.5rem;
  align-items: flex-start;

  & > *:last-child {
    margin-left: auto;
  }
  
  & > ${FlexboxVertical} > button {
    margin-bottom: 1.25rem;
  }
`;

export interface CTAButtonProps {
  onCTAButtonClick?: () => void;
  ctaButtonTitle?: string;
  ctaButtonState?: 'visible' | 'disabled' | 'hidden';
}

export interface ProductDetailSummaryProps {
  product?: Product;
  onBackButtonClick?: () => void;
  onSkipButtonClick?: () => void;
  ctaButtons?: CTAButtonProps[];
}

export const ProductDetailSummary: FC<ProductDetailSummaryProps> = ({ product, ...props }) => {
  const panelViewRef = useRef<HTMLDivElement | null>(null);
  const accentColor = parseProductColorSet(product?.metaProduct?.colorSet);

  return (
    <SummaryWrapper>

      {
        (product?.metaProduct?.overview?.list && product?.metaProduct?.overview?.list.length > 0 && product?.metaProduct?.overview?.list[0].length > 0) && <>
          <FontColor color={accentColor}><Headline2>Auf einen Blick</Headline2></FontColor>

          <PanelWithGradient ref={panelViewRef} product={product}>
            <AnimatedWhenVisible viewRef={panelViewRef}>
              {(readyToAnimate) => (
                <>
                <AnimatedContainer active={readyToAnimate} delay={0}>
                  <Headline4 dangerouslySetInnerHTML={{ __html: product?.metaProduct?.overview?.title }} />
                </AnimatedContainer>
                <ul>
                  {
                    product?.metaProduct?.overview?.list?.map((item, index, array) => (
                      <AnimatedContainer active={readyToAnimate} delay={100 * (index+1)}>
                        <ListItem>
                          <Icon icon={'check-bold'} size={16} />
                          <TextDefault dangerouslySetInnerHTML={{ __html: item }} />
                        </ListItem>
                      </AnimatedContainer>
                    ))
                  }
                </ul>
                </>
              )}
            </AnimatedWhenVisible>
          </PanelWithGradient>
        </>
      }

      <ButtonsWrapper>
        <Button type={'primary'} onClick={() => props.onBackButtonClick && props.onBackButtonClick()}>Zurück</Button>
        <FlexboxVertical>
          {
            (props.ctaButtons || []).map(({ ctaButtonState = 'hidden', onCTAButtonClick, ctaButtonTitle }) => (
              <Button
                style={ctaButtonState === 'hidden' && { visibility: 'hidden' }}
                disabled={ctaButtonState === 'disabled'}
                type={'secondary'}
                onClick={() => onCTAButtonClick && onCTAButtonClick()}
              >{ctaButtonTitle || 'Weiter'}</Button>
            ))
          }
          {
            props.onSkipButtonClick &&
            <LinkButton onChangeHandler={() => props.onSkipButtonClick && props.onSkipButtonClick()}>
              <TextTiny>Produktauswahl überspringen</TextTiny>
            </LinkButton>
          }
        </FlexboxVertical>
      </ButtonsWrapper>

    </SummaryWrapper>
  );
};
